/**
 * @file components/settings/LookupAdminPage.tsx
 * @description 客戶字典（lookup）通用維護頁元件。
 *
 * 三張字典（驗證資格 / 驗證選項 / 合約來源）的後台維護頁結構幾乎一致：
 *   - 列表：name / description / sortOrder / isActive
 *   - 新增 / 編輯 Modal：name + description + sortOrder + isActive
 *   - 軟刪除（Popconfirm）
 * 故抽成共用元件，三個 route 檔只需傳入 resource 與 label 等少量配置。
 */

import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import { message } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { Edit3, Plus, Search, Tag as TagIcon, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, PageMetaItem, FilterBar, StatusPill } from '@/components/shell';
import type { LookupItem, LookupListResponse } from '@/hooks/useClientLookups';
import { color, font, text, weight } from '@/styles/tokens';

interface LookupAdminPageProps {
  /** 後端 settings 路徑（如 'client-qualifications'），對應 /api/settings/{resource} */
  resource: string;
  /** 顯示名稱（如「驗證資格」），用於頁面標題與 Modal */
  resourceLabel: string;
  /** 頁面副標題說明 */
  description: string;
  /** name 欄位最大長度（與 DB 對齊：qualification 50 / option 30 / source 50） */
  nameMaxLength: number;
  /** 自定 meta icon（預設 TagIcon） */
  metaIcon?: ReactNode;
  /**
   * 額外表格欄（插在「狀態」與「操作」之間）。
   * 供帶額外欄位的字典使用，如驗證類別的 scheme、驗證方案的 code/legalBasis。
   */
  extraTableColumns?: ColumnsType<LookupItem>;
  /**
   * 額外表單欄位（插在「描述」之後）。
   * 表單以整個 values 提交，額外欄位（scheme/code/legalBasis）會自動隨表單送出，
   * 編輯時 handleEdit 的 setFieldsValue({ ...record }) 也會自動回填。
   */
  extraFormFields?: ReactNode;
}

/**
 * 通用 lookup 維護頁。
 */
export function LookupAdminPage({
  resource,
  resourceLabel,
  description,
  nameMaxLength,
  metaIcon,
  extraTableColumns,
  extraFormFields,
}: LookupAdminPageProps) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LookupItem | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm<Record<string, unknown>>();

  /** 列表查詢（不傳 activeOnly = 顯示全部，含停用） */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-lookup', resource, { activeOnly: false }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('pageSize', '500');
      return apiClient.get<LookupListResponse>(`/api/settings/${resource}?${params.toString()}`);
    },
  });

  const allItems = data?.items ?? [];
  // 客端搜尋（資料量小，500 筆內完全夠）
  const items = searchText
    ? allItems.filter(
        (it) =>
          it.name.toLowerCase().includes(searchText.toLowerCase()) ||
          (it.description ?? '').toLowerCase().includes(searchText.toLowerCase()),
      )
    : allItems;
  const total = allItems.length;
  const activeCount = allItems.filter((x) => x.isActive).length;

  /** 新增 */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post(`/api/settings/${resource}`, v),
    onSuccess: () => {
      message.success(`${resourceLabel}已新增`);
      void qc.invalidateQueries({ queryKey: ['settings-lookup', resource] });
      setModalOpen(false);
      form.resetFields();
    },
  });

  /** 更新 */
  const update = useMutation({
    mutationFn: (v: unknown) => apiClient.patch(`/api/settings/${resource}/${editing!.id}`, v),
    onSuccess: () => {
      message.success(`${resourceLabel}已更新`);
      void qc.invalidateQueries({ queryKey: ['settings-lookup', resource] });
      setModalOpen(false);
      form.resetFields();
    },
  });

  /** 軟刪除 */
  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/settings/${resource}/${id}`),
    onSuccess: () => {
      message.success(`${resourceLabel}已刪除`);
      void qc.invalidateQueries({ queryKey: ['settings-lookup', resource] });
    },
  });

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ sortOrder: 0, isActive: true });
    setModalOpen(true);
  };

  const handleEdit = (record: LookupItem) => {
    setEditing(record);
    // LookupItem 缺 index signature，無法直接 assign 到 Form 的泛型參數，
    // 改用 spread 把欄位平展為 Record<string, unknown>。
    form.setFieldsValue({ ...record });
    setModalOpen(true);
  };

  const handleFinish = (values: Record<string, unknown>) => {
    if (editing) update.mutate(values);
    else create.mutate(values);
  };

  const columns: ColumnsType<LookupItem> = [
    {
      title: '名稱',
      dataIndex: 'name',
      render: (v: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      render: (v: string | null) =>
        v ? (
          <span style={{ color: color.text.secondary }}>{v}</span>
        ) : (
          <span style={{ color: color.text.muted }}>—</span>
        ),
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 80,
      align: 'right',
      render: (v: number) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v}
        </span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      width: 90,
      render: (v: boolean) =>
        v ? (
          <StatusPill tone="success" size="sm" dot>
            啟用
          </StatusPill>
        ) : (
          <StatusPill tone="muted" size="sm">
            停用
          </StatusPill>
        ),
    },
    // 額外欄位（如驗證類別 scheme、驗證方案 code/法規依據）插在狀態與操作之間
    ...(extraTableColumns ?? []),
    {
      title: '操作',
      key: '_actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="編輯">
            <Button
              type="text"
              size="small"
              icon={<Edit3 size={14} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={`確定刪除「${record.name}」？`}
            description="既有客戶資料若引用此項仍會保留原值。"
            onConfirm={() => remove.mutate(record.id)}
            okText="刪除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="刪除">
              <Button type="text" size="small" danger icon={<Trash2 size={14} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title={`${resourceLabel}維護`}
        description={description}
        meta={
          <>
            <PageMetaItem
              icon={metaIcon ?? <TagIcon size={13} />}
              label="總筆數"
              value={`${total} 筆`}
            />
            <PageMetaItem label="啟用中" value={`${activeCount} 筆`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增{resourceLabel}
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋名稱 / 描述"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
          />
        }
      >
        <Table<LookupItem>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 700 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 筆資料`,
            defaultPageSize: 20,
          }}
        />
      </ListPageShell>

      <Modal
        title={`${editing ? '編輯' : '新增'}${resourceLabel}`}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ marginTop: 16 }}
          initialValues={{ sortOrder: 0, isActive: true }}
        >
          <Form.Item
            name="name"
            label={`${resourceLabel}名稱`}
            rules={[{ required: true, max: nameMaxLength }]}
          >
            <Input maxLength={nameMaxLength} placeholder={`請輸入${resourceLabel}名稱`} />
          </Form.Item>

          <Form.Item name="description" label="描述（可選）">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>

          {/* 額外表單欄位（如驗證類別 scheme、驗證方案 code/法規依據） */}
          {extraFormFields}

          <Form.Item
            name="sortOrder"
            label="排序權重"
            tooltip="數字越小越靠前；建議以 10 為間隔（10、20、30…）方便日後插入"
            rules={[{ required: true }]}
          >
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="啟用狀態"
            valuePropName="checked"
            tooltip="停用後此項不會出現在編輯客戶的下拉選單；既有客戶資料保留原值"
          >
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={create.isPending || update.isPending}
              >
                {editing ? '儲存' : '新增'}
              </Button>
              <Button onClick={() => setModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
